import { AppConfigService } from "@/services/app-config.service";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import {
  Address,
  Document,
  DocumentCategory,
  DocumentIdentifier,
  DocumentSummary,
  DocumentType,
  DropBoxDocument,
  LandRegistry,
  PersonGender,
  PersonType,
  Photo,
  PhotoCategory,
  PropertyCategory,
  PropertyEquipmentType,
  PropertyRoomExposure,
  PropertyRoomOpeningType,
  PropertyRoomType,
  PropertyType,
  RealProperty,
  SellingStatus,
  WorkType,
} from "@eemo/api-client";
import * as mime from "mime-types";
import { environment } from "src/environments/environment";
import { getApiBaseUrl, getBaseAbsoluteUrl } from "./app.constants";
import { UserHelper } from "./user.helper";

@Injectable({ providedIn: "root" })
export class PropertyHelper {
  constructor(
    private http: HttpClient,
    private sanitizer: DomSanitizer,
    private userHelper: UserHelper,
    private appConfigService: AppConfigService
  ) {}

  public static getPropertyTypes(): PropertyType[] {
    return [
      PropertyType.HOUSE,
      PropertyType.FLAT,
      PropertyType.GARAGE,
      PropertyType.BASEMENT,
      PropertyType.PARKING,
      PropertyType.BUILDING,
      PropertyType.BUSINESS,
      PropertyType.FARM,
      PropertyType.PUBLIC,
      PropertyType.LAND,
      PropertyType.CONDOMINIUM,
    ];
  }

  public static getPropertyTypesForParent(
    parentType: PropertyType
  ): PropertyType[] {
    let types: PropertyType[];
    switch (parentType) {
      case PropertyType.GARAGE:
      case PropertyType.PARKING:
      case PropertyType.BASEMENT:
      case PropertyType.LAND:
        // no children allowed for land, garage, parking and basement
        return [];
      case PropertyType.BUSINESS:
      case PropertyType.FLAT:
        types = [
          PropertyType.GARAGE,
          PropertyType.BASEMENT,
          PropertyType.PARKING,
        ];
        break;
      case PropertyType.HOUSE:
        types = [
          PropertyType.GARAGE,
          PropertyType.BASEMENT,
          PropertyType.PARKING,
          PropertyType.LAND,
          PropertyType.FLAT,
        ];
        break;
      case PropertyType.BUILDING:
        types = [
          PropertyType.FLAT,
          PropertyType.BUSINESS,
          PropertyType.HOUSE,
          PropertyType.GARAGE,
          PropertyType.BASEMENT,
          PropertyType.PARKING,
        ];
        break;
      case PropertyType.PUBLIC:
      case PropertyType.FARM:
      case PropertyType.CONDOMINIUM:
        types = [
          PropertyType.BUILDING,
          PropertyType.FLAT,
          PropertyType.BUSINESS,
          PropertyType.HOUSE,
          PropertyType.GARAGE,
          PropertyType.BASEMENT,
          PropertyType.PARKING,
          PropertyType.LAND,
        ];
        break;
    }
    return types.sort(this.sortPropertyTypeList);
  }

  public static sortPropertyTypeList(a: PropertyType, b: PropertyType): number {
    const orderA = PropertyHelper.getPropertyTypes().indexOf(a);
    const orderB = PropertyHelper.getPropertyTypes().indexOf(b);
    if (orderA > orderB) {
      return 1;
    }
    if (orderA < orderB) {
      return -1;
    }
    return 0;
  }

  public static sortPropertiesByName(a: RealProperty, b: RealProperty): number {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
  }

  public static sortPropertiesByOwnerName(
    a: RealProperty,
    b: RealProperty
  ): number {
    const nameA = a.owner.name.toUpperCase();
    const nameB = b.owner.name.toUpperCase();
    return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
  }

  static getSlugPropertyName(name: string) {
    return name ? name.toLowerCase().replace(/\s/g, "-") : "--";
  }

  sortPropertyList(props: RealProperty[]): RealProperty[] {
    let sortedProperties: RealProperty[] = [];
    const ownedProperties: RealProperty[] = [];
    const delegatedProperties: RealProperty[] = [];
    const viewedProperties: RealProperty[] = [];

    props.forEach((prop) => {
      if (this.userHelper.isPropertyOwner(prop)) {
        ownedProperties.push(prop);
      } else if (this.userHelper.canUpdateProperty(prop)) {
        delegatedProperties.push(prop);
      } else {
        viewedProperties.push(prop);
      }
    });
    ownedProperties.sort(PropertyHelper.sortPropertiesByName);
    delegatedProperties.sort(PropertyHelper.sortPropertiesByOwnerName);
    viewedProperties.sort(PropertyHelper.sortPropertiesByOwnerName);

    sortedProperties = sortedProperties.concat(
      ownedProperties,
      delegatedProperties,
      viewedProperties
    );
    return sortedProperties;
  }

  getPropertyTypes(): PropertyType[] {
    return PropertyHelper.getPropertyTypes();
  }

  getPropertyTypesForParent(parentType: PropertyType): PropertyType[] {
    return PropertyHelper.getPropertyTypesForParent(parentType);
  }

  isHouse(prop: RealProperty): boolean {
    return PropertyType.HOUSE === prop.type;
  }

  isFlat(prop: RealProperty): boolean {
    return PropertyType.FLAT === prop.type;
  }

  isGarage(prop: RealProperty): boolean {
    return PropertyType.GARAGE === prop.type;
  }

  isBuilding(prop: RealProperty): boolean {
    return PropertyType.BUILDING === prop.type;
  }

  isBusiness(prop: RealProperty): boolean {
    return PropertyType.BUSINESS === prop.type;
  }

  isFarm(prop: RealProperty): boolean {
    return PropertyType.FARM === prop.type;
  }

  isPublic(prop: RealProperty): boolean {
    return PropertyType.PUBLIC === prop.type;
  }

  isLand(prop: RealProperty): boolean {
    return PropertyType.LAND === prop.type;
  }

  isBasement(prop: RealProperty): boolean {
    return PropertyType.BASEMENT === prop.type;
  }

  isParking(prop: RealProperty): boolean {
    return PropertyType.PARKING === prop.type;
  }

  isCondo(prop: RealProperty): boolean {
    return PropertyType.CONDOMINIUM === prop.type;
  }

  isInCondo(prop: RealProperty): boolean {
    return prop.condo !== undefined;
  }

  getOwnersNameFull(prop: RealProperty): string {
    let name = UserHelper.userSummmaryDisplayName(prop.owner);
    if (prop.coOwner) {
      if (prop.owner.name === prop.coOwner.name) {
        // same family name
        return "Mr et Mme " + prop.owner.name;
      } else {
        name += " et " + UserHelper.userSummmaryDisplayName(prop.coOwner);
      }
    }
    return name;
  }

  getOwnersName(prop: RealProperty) {
    return this.getOwnersNameWithSize(prop, 32);
  }

  getOwnersNameShort(prop: RealProperty) {
    return this.getOwnersNameWithSize(prop, 25);
  }

  getOwnersNameWithSize(prop: RealProperty, maxSize: number): string {
    let name = this.getOwnersNameFull(prop);
    if (name.length > maxSize) {
      name =
        prop.owner.firstName.slice(0, 1).toUpperCase() + ". " + prop.owner.name;
      if (prop.coOwner) {
        name +=
          " et " +
          prop.coOwner.firstName.slice(0, 1).toUpperCase() +
          ". " +
          prop.coOwner.name;
      }
    }
    return name;
  }

  getSharedUserNameFull(prop: RealProperty): string {
    return UserHelper.userSummmaryDisplayName(prop.sharedBy);
  }
  getSharedUserName(prop: RealProperty) {
    return this.getSharedUserNameWithSize(prop, 32);
  }

  getSharedUserNameShort(prop: RealProperty) {
    return this.getSharedUserNameWithSize(prop, 25);
  }

  getSharedUserNameWithSize(prop: RealProperty, maxSize: number) {
    let name = UserHelper.userSummmaryDisplayName(prop.sharedBy);
    if (name.length > maxSize) {
      name =
        prop.sharedBy.firstName.slice(0, 1).toUpperCase() +
        ". " +
        prop.sharedBy.name;
    }
    return name;
  }

  getTypeName(type: PropertyType): string {
    switch (type) {
      case PropertyType.HOUSE:
        return "Maison";
      case PropertyType.FLAT:
        return "Appartement";
      case PropertyType.GARAGE:
        return "Garage";
      case PropertyType.BUILDING:
        return "Immeuble";
      case PropertyType.BUSINESS:
        return "Locaux professionnels";
      case PropertyType.FARM:
        return "Exploitation agricole";
      case PropertyType.PUBLIC:
        return "Bâtiment public";
      case PropertyType.LAND:
        return "Terrain";
      case PropertyType.BASEMENT:
        return "Cave";
      case PropertyType.PARKING:
        return "Parking";
      case PropertyType.CONDOMINIUM:
        return "Copropriété";
      default:
        return "Inconnu";
    }
  }

  getCategories(): string[] {
    return Object.keys(PropertyCategory);
  }

  getCategoryName(category: PropertyCategory, propType?: PropertyType) {
    // manage specific case
    if (
      propType &&
      propType === PropertyType.CONDOMINIUM &&
      category === PropertyCategory.PEOPLE
    ) {
      return "Syndic";
    }
    switch (category) {
      case PropertyCategory.LOCATION:
        return "Localisation";
      case PropertyCategory.BUILDING:
        return "Construction";
      case PropertyCategory.ENERGY:
        return "Énergie";
      case PropertyCategory.WORK:
        return "Travaux";
      case PropertyCategory.EQUIPMENT:
        return "Équipements";
      case PropertyCategory.FINANCIAL:
        return "Finance";
      case PropertyCategory.PEOPLE:
        return "Confidentiel";
      default:
        return "Catégorie inconnue";
    }
  }

  getDocumentCategories(): DocumentCategory[] {
    // build the aray manually to control the order
    const categ: DocumentCategory[] = [];
    categ.push(DocumentCategory.LOCATION);
    categ.push(DocumentCategory.BUILDING);
    categ.push(DocumentCategory.ENERGY);
    categ.push(DocumentCategory.FINANCIAL);
    categ.push(DocumentCategory.PEOPLE);
    categ.push(DocumentCategory.CONDOMINIUM);
    categ.push(DocumentCategory.WORK);
    categ.push(DocumentCategory.EQUIPMENT);
    categ.push(DocumentCategory.MAINTENANCE);
    categ.push(DocumentCategory.BOOKLET);
    return categ;
  }

  getDocumentCategoryName(category: DocumentCategory, propType?: PropertyType) {
    // manage specific case
    if (
      propType &&
      propType === PropertyType.CONDOMINIUM &&
      category === DocumentCategory.PEOPLE
    ) {
      return "Syndic";
    }
    switch (category) {
      case DocumentCategory.LOCATION:
        return "Localisation";
      case DocumentCategory.BUILDING:
        return "Construction";
      case DocumentCategory.ENERGY:
        return "Énergie";
      case DocumentCategory.WORK:
        return "Travaux";
      case DocumentCategory.EQUIPMENT:
        return "Équipements";
      case DocumentCategory.FINANCIAL:
        return "Finance";
      case DocumentCategory.PEOPLE:
        return "Confidentiel";
      case DocumentCategory.CONDOMINIUM:
        return "Copropriété";
      case DocumentCategory.MAINTENANCE:
        return "Entretiens";
      case DocumentCategory.BOOKLET:
        return "Carnet d'information logement (CIL)";
      default:
        return "Catégorie inconnue";
    }
  }

  getAddressSummary(address: Address) {
    return address.street + " " + this.getAddressCityName(address);
  }

  getAddressLine1(address: Address) {
    if (address) {
      return address.street;
    }
    return "";
  }

  getAddressLine2(address: Address) {
    if (address) {
      if (address.complement) {
        return address.complement;
      } else {
        return this.getAddressCityName(address);
      }
    }
    return "";
  }

  getAddressLine3(address: Address) {
    if (address && address.complement) {
      return this.getAddressCityName(address);
    }
    return "";
  }

  getAddressLines(address: Address, withComplement?: boolean): string[] {
    const lines: string[] = [];
    if (address) {
      lines.push(address.street);
      if (withComplement && address.complement) {
        lines.push(address.complement);
      }
      lines.push(this.getAddressCityName(address));
    }
    return lines;
  }

  getAddressCityName(address: Address) {
    if (address) {
      return address.postalCode + " " + address.city;
    }
    return "";
  }

  getPersonTypes(): string[] {
    return Object.keys(PersonType);
  }

  getPersonTypeName(type: PersonType) {
    switch (type) {
      case PersonType.OWNER:
        return "Propriétaire";
      case PersonType.RENTER:
        return "Locataire";
      case PersonType.SPOUSE:
        return "Conjoint";
      case PersonType.CHILD:
        return "Enfant";
      case PersonType.OTHER:
        return "Autre";
      default:
        return "Type inconnu";
    }
  }

  getPersonGenders(): string[] {
    return Object.keys(PersonGender);
  }

  getPersonGenderName(type: PersonGender) {
    switch (type) {
      case PersonGender.MAN:
        return "Homme";
      case PersonGender.WOMAN:
        return "Femme";
      default:
        return "Genre inconnu";
    }
  }

  getPhotoCategories(): string[] {
    return Object.keys(PhotoCategory);
  }

  getPhotoCategoryName(category: PhotoCategory) {
    switch (category) {
      case PhotoCategory.BUILDING:
        return "Construction";
      case PhotoCategory.INSIDE:
        return "Intérieur";
      case PhotoCategory.OUTSIDE:
        return "Extérieur";
      default:
        return "Catégorie inconnue";
    }
  }

  mapToDocumentSummary(doc: Document): DocumentSummary {
    return {
      uid: doc.uid,
      type: doc.type,
      name: doc.name,
      size: doc.size,
      mimeType: doc.mimeType,
    };
  }

  getDocumentsTypes(): string[] {
    return Object.keys(DocumentType);
  }

  getDocumentTypeName(type: DocumentType) {
    switch (type) {
      case DocumentType.MAP:
        return "Plans";
      case DocumentType.OFFICIAL:
        return "Document officiel";
      case DocumentType.CERTIFICATE:
        return "Certificat";
      case DocumentType.CONTRACT:
        return "Document contractuel";
      case DocumentType.NOTICE:
        return "Avis d'imposition";
      case DocumentType.DOCUMENTATION:
        return "Documentation";
      case DocumentType.INVOICE:
        return "Facture";
      case DocumentType.QUOTE:
        return "Devis";
      case DocumentType.OTHER:
        return "Autre";
      default:
        return "Type inconnu";
    }
  }

  getDocumentIdentifierName(identifier: DocumentIdentifier) {
    switch (identifier) {
      case DocumentIdentifier.ENERGY_DIAGNOSTICS:
        return "Diagnostic de Performance Énergétique";
      case DocumentIdentifier.LOCAL_URBAN_PLAN:
        return "Plan Local d'Urbanisme (réglement)";
      case DocumentIdentifier.LOCAL_URBAN_PLAN_FULL:
        return "Plan Local d'Urbanisme (complet)";
      case DocumentIdentifier.POLLUTION_RISKS_STATE:
        return "État des risques et pollutions";
      case DocumentIdentifier.REGISTRY_MAP:
        return "Plan cadastral";
      case DocumentIdentifier.PROPERTY_BOOKLET:
        return "Carnet d'information logement";
      case DocumentIdentifier.NONE:
        return "Aucun";
      default:
        return "Identifiant inconnu";
    }
  }

  getPropertyRoomTypes(): string[] {
    return Object.values(PropertyRoomType);
  }

  getPropertyRoomTypeName(type: PropertyRoomType) {
    switch (type) {
      case "HALL":
        return "Hall";
      case "LIVING_ROOM":
        return "Séjour";
      case "KITCHEN":
        return "Cuisine";
      case "BEDROOM":
        return "Chambre";
      case "BATHROOM":
        return "Salle de bain";
      case "DRESSING":
        return "Dressing";
      case "TOILET":
        return "Toilettes";
      case "GARAGE":
        return "Dressing";
      case "STORAGE_ROOM":
        return "Cagibi";
      case "BASEMENT":
        return "Cave";
      case "OTHER":
        return "Autre";
      default:
        return "Type inconnu";
    }
  }

  getPropertyRoomLevels(): number[] {
    return [0, 1, 2, 3, -1, -2];
  }

  getPropertyRoomLevelName(level: number): string {
    switch (level) {
      case -2:
        return "2ème sous-sol";
      case -1:
        return "Sous-sol";
      case 0:
        return "Rez de chaussée";
      case 1:
        return "1er étage";
      case 2:
        return "2ème étage";
      case 3:
        return "3ème étage";
      default:
        return level + "ème étage";
    }
  }

  getPropertyRoomExposures(): string[] {
    return Object.keys(PropertyRoomExposure);
  }

  getPropertyRoomExposureName(exposure: PropertyRoomExposure) {
    switch (exposure) {
      case PropertyRoomExposure.N:
        return "Nord";
      case PropertyRoomExposure.NE:
        return "Nord-Est";
      case PropertyRoomExposure.E:
        return "Est";
      case PropertyRoomExposure.SE:
        return "Sud-Est";
      case PropertyRoomExposure.S:
        return "Sud";
      case PropertyRoomExposure.SW:
        return "Sud-ouest";
      case PropertyRoomExposure.W:
        return "Ouest";
      case PropertyRoomExposure.NW:
        return "Nord-Ouest";
      default:
        return "Exposition inconnue";
    }
  }

  getPropertyRoomOpeningTypes(): string[] {
    return Object.values(PropertyRoomOpeningType);
  }

  getPropertyRoomOpeningTypeName(type: PropertyRoomOpeningType) {
    switch (type) {
      case "WINDOW":
        return "Fenêtre";
      case "FRENCH_WINDOW":
        return "Porte fenêtre";
      case "SKYLIGHT":
        return "Velux";
      default:
        return "Type inconnu";
    }
  }

  getWorkTypes(): string[] {
    return Object.values(WorkType);
  }

  getWorkTypeName(type: WorkType) {
    switch (type) {
      case "BUILDING":
        return "Construction";
      case "RENOVATION":
        return "Rénovation";
      case "ENERGY":
        return "Énergétique";
      case "SANITATON":
        return "Assainissement";
      case "OUTSIDE":
        return "Extérieur";
      case "OTHER":
        return "Autre";
      default:
        return "Type inconnu";
    }
  }

  getEquipmentTypes(): string[] {
    return Object.values(PropertyEquipmentType);
  }

  isDocumentViewable(doc: Document | DropBoxDocument | DocumentSummary) {
    return (
      doc.mimeType === "application/pdf" ||
      doc.mimeType === "image/png" ||
      doc.mimeType === "image/jpeg"
    );
  }

  isDocumentViewableInNewPage(doc: Document | DropBoxDocument) {
    // feature disable for the moment
    return false;
  }

  isDocumentViewableInIFrame(doc: Document | DropBoxDocument) {
    return (
      this.appConfigService.isDesktop ||
      doc.mimeType === "image/png" ||
      doc.mimeType === "image/jpeg"
    );
  }

  isDocumentViewableInGoogleDoc(doc: Document | DropBoxDocument) {
    /*return (
      (this.appConfigService.isMobileApp || this.appConfigService.isMobileWeb) && doc.mimeType === 'application/pdf'
    );*/
    // google doc is disabled due to an unmanageable bug in safari
    return false;
  }

  isDocumentViewableInPdfViewer(doc: Document | DropBoxDocument) {
    return (
      this.appConfigService.isMobileOrTablet &&
      doc.mimeType === "application/pdf"
    );
  }

  isDocumentImage(doc: Document | DropBoxDocument) {
    return doc.mimeType === "image/png" || doc.mimeType === "image/jpeg";
  }

  getBadgeImageName(propType: PropertyType): string {
    switch (propType) {
      case PropertyType.HOUSE:
        return "house";
      case PropertyType.FLAT:
        return "apartment";
      case PropertyType.GARAGE:
        return "garage";
      case PropertyType.BUILDING:
        return "domain";
      case PropertyType.BUSINESS:
        return "store_mall_directory";
      case PropertyType.FARM:
        return "agriculture";
      case PropertyType.PUBLIC:
        return "museum";
      case PropertyType.LAND:
        return "terrain";
      case PropertyType.PARKING:
        return "directions_car";
      case PropertyType.BASEMENT:
        return "stairs";
      case PropertyType.CONDOMINIUM:
        return "holiday_village";
      default:
        return "house";
    }
  }

  getEmptyMainPhotoImage(): string {
    return "assets/images/no_photo.jpg";
  }

  getPhotoImageUrl(photo: Photo): string {
    return getApiBaseUrl() + "/photos/" + photo.uid + "/image";
  }

  getPhotoFilename(photo: Photo): string {
    return photo.name + "." + this.getFileExtensionFromMimeType(photo.mimeType);
  }

  getPhotoImageUrlWithSize(photo: Photo, size: string): string {
    return getApiBaseUrl() + "/photos/" + photo.uid + "/image?size=" + size;
  }

  getQrCodeImage(uid: string): string {
    return getApiBaseUrl() + "/properties/" + uid + "/qrcode";
  }

  getQrCodeImageWithoutLogo(uid: string): string {
    return getApiBaseUrl() + "/properties/" + uid + "/qrcode?withLogo=false";
  }

  getDocumentFilename(doc: Document | DropBoxDocument | DocumentSummary) {
    return doc.name + "." + this.getFileExtensionFromMimeType(doc.mimeType);
  }

  getDocumentUrl(doc: Document | DocumentSummary): string {
    return getApiBaseUrl() + "/vault/documents/" + doc.uid + "/file";
  }

  getDocumentFullUrl(doc: Document): string {
    return getBaseAbsoluteUrl() + this.getDocumentUrl(doc);
  }

  getDocumentAnonymousUrl(doc: Document, anonymousToken: string): string {
    return (
      getApiBaseUrl() +
      "/vault/documents/" +
      doc.uid +
      "/anonymous/file/" +
      anonymousToken
    );
  }

  getGoogleDocumentPreviewUrl(doc: Document, anonymousToken: string): string {
    return (
      environment.core.apiGoogleDocs +
      "/gview?embedded=true&url=" +
      getBaseAbsoluteUrl() +
      this.getDocumentAnonymousUrl(doc, anonymousToken) +
      "/file.pdf"
    );
  }

  getGoogleDropBoxDocumentPreviewUrl(
    doc: DropBoxDocument,
    anonymousToken: string
  ): string {
    return (
      environment.core.apiGoogleDocs +
      "/gview?embedded=true&url=" +
      getBaseAbsoluteUrl() +
      this.getDropBoxDocumentAnonymousUrl(doc, anonymousToken) +
      "/file.pdf"
    );
  }

  getGooglePreviewUrl(url: string): string {
    return environment.core.apiGoogleDocs + "/gview?embedded=true&url=" + url;
  }

  getExportPropertyDocumentsUrl(prop: RealProperty): string {
    return (
      getApiBaseUrl() +
      "/vault/documents/properties/" +
      prop.uid +
      "/export/download"
    );
  }

  getDropBoxDomain() {
    return environment.dropBoxDomain;
  }

  getDropBoxAddress(prop: RealProperty) {
    if (prop.certified) {
      return prop.eemoId + "@" + environment.dropBoxDomain;
    }
    return "";
  }

  getDropBoxDocumentFullUrl(doc: DropBoxDocument): string {
    return getBaseAbsoluteUrl() + this.getDropBoxDocumentUrl(doc);
  }

  getDropBoxDocumentUrl(doc: DropBoxDocument): string {
    return getApiBaseUrl() + "/vault/dropbox/" + doc.uid + "/file";
  }

  getDropBoxDocumentAnonymousUrl(
    doc: DropBoxDocument,
    anonymousToken: string
  ): string {
    return (
      getApiBaseUrl() +
      "/vault/dropbox/" +
      doc.uid +
      "/anonymous/file/" +
      anonymousToken
    );
  }

  cleanUrl(url: string): SafeResourceUrl {
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  getDefaultNameFromFilename(filename: string) {
    const fileTab = filename.split(".");
    if (fileTab.length > 1) {
      // the filename has an extension -> to remove
      fileTab.pop();
    }
    const name = fileTab.join(".");
    return name.trim().substring(0, 49);
  }

  getFileExtensionFromMimeType(mimeType: string) {
    return mime.extension(mimeType);
  }

  getVCardUrl(prop: RealProperty): string {
    return "/property/vcard/" + prop.eemoId;
  }

  getNfcUrl(prop: RealProperty): string {
    return "/property/" + prop.eemoId;
  }

  getIdCardDocumentUrl(prop: RealProperty): string {
    return getApiBaseUrl() + "/certifications/" + prop.uid + "/document/idcard";
  }

  getIdCardDocumentPrintUrl(prop: RealProperty): string {
    return getApiBaseUrl() + "/certifications/" + prop.uid + "/print/idcard";
  }

  getCertificateDocumentUrl(prop: RealProperty): string {
    return (
      getApiBaseUrl() + "/certifications/" + prop.uid + "/document/certificate"
    );
  }

  getCertificateDocumentPrintUrl(prop: RealProperty): string {
    return (
      getApiBaseUrl() + "/certifications/" + prop.uid + "/print/certificate"
    );
  }

  getOwnerAddressDocumentPrintUrl(prop: RealProperty): string {
    return getApiBaseUrl() + "/certifications/" + prop.uid + "/print/address";
  }

  getFileTooltipFromMimeType(mimeType: string) {
    if (mimeType.includes("image")) {
      return "Image";
    }
    if (mimeType.includes("audio")) {
      return "Audio";
    }
    if (mimeType.includes("video")) {
      return "Vidéo";
    }
    switch (mimeType) {
      case "application/pdf":
        return "PDF";
      case "application/msword":
      case "application/vnd.ms-word":
      case "application/vnd.oasis.opendocument.text":
      case "application/vnd.openxmlformats-officedocument.wordprocessingml":
        return "Word";
      case "application/vnd.ms-excel":
      case "application/vnd.openxmlformats-officedocument.spreadsheetml":
      case "application/vnd.oasis.opendocument.spreadsheet":
        return "Excel";
      case "application/vnd.ms-powerpoint":
      case "application/vnd.openxmlformats-officedocument.presentationml":
      case "application/vnd.oasis.opendocument.presentation":
        return "PowerPoint";
      case "text/plain":
        return "Texte";
      case "text/html":
      case "application/json":
      case "application/xml":
        return "Code";
      case "application / gzip":
      case "application/zip":
        return "Code";
      default:
        return "Fichier";
    }
  }

  getFileIconFromMimeType(mimeType: string) {
    const iconClasses: { [key: string]: string } = {};
    // Media
    iconClasses.image = "fa-file-image";
    iconClasses.audio = "fa-file-audio";
    iconClasses.video = "fa-file-video";
    // Documents
    iconClasses["application/pdf"] = "fa-file-pdf";
    iconClasses["application/msword"] = "fa-file-word";
    iconClasses["application/vnd.ms-word"] = "fa-file-word";
    iconClasses["application/vnd.oasis.opendocument.text"] = "fa-file-word";
    iconClasses[
      "application/vnd.openxmlformats-officedocument.wordprocessingml"
    ] = "fa-file-word";
    iconClasses["application/vnd.ms-excel"] = "fa-file-excel";
    iconClasses["application/vnd.openxmlformats-officedocument.spreadsheetml"] =
      "fa-file-excel";
    iconClasses["application/vnd.oasis.opendocument.spreadsheet"] =
      "fa-file-excel";
    iconClasses["application/vnd.ms-powerpoint"] = "fa-file-powerpoint";
    iconClasses[
      "application/vnd.openxmlformats-officedocument.presentationml"
    ] = "fa-file-powerpoint";
    iconClasses["application/vnd.oasis.opendocument.presentation"] =
      "fa-file-powerpoint";
    iconClasses["text/plain"] = "fa-file-alt";
    iconClasses["text/html"] = "fa-file-code";
    iconClasses["application/json"] = "fa-file-code";
    iconClasses["application/xml"] = "fa-file-code";
    // Archives
    iconClasses["application/gzip"] = "fa-file-archive";
    iconClasses["application/zip"] = "fa-file-archive";

    let iconClass = "fa-file";
    for (const type of Object.keys(iconClasses)) {
      if (mimeType.includes(type)) {
        iconClass = iconClasses[type];
      }
    }
    return "fas " + iconClass;
  }

  public isPageCondoManagementAllowed(): boolean {
    return this.userHelper.isAdmin() || this.userHelper.isRealEstate();
  }

  public getPropRouterLink(prop: RealProperty): string[] {
    let target = "/property";
    if (
      prop.type === PropertyType.CONDOMINIUM &&
      this.isPageCondoManagementAllowed()
    ) {
      target = "/condo-mngt";
    }
    return [target, prop.uid];
  }

  getLandRegistryDisplayName(registry: LandRegistry) {
    return this.getLandRegistryIdentifierShort(registry);
  }

  getLandRegistryIdentifierShort(registry: LandRegistry) {
    return registry.prefix + "-" + registry.section + "-" + registry.plot;
  }

  getLandRegistryIdentifier(registry: LandRegistry) {
    return (
      registry.inseeCode + registry.prefix + registry.section + registry.plot
    );
  }

  isOnSelling(prop: RealProperty) {
    return (
      !prop.demo &&
      prop.onGoingSelling != null &&
      prop.onGoingSelling?.status !== SellingStatus.CANCELED &&
      prop.onGoingSelling?.status !== SellingStatus.SOLD
    );
  }
}
